// Stripe money helpers. Stripe stores charge/price amounts in the currency's
// smallest unit — "cents" for most currencies (amount = major × 100), but for
// zero-decimal currencies the amount IS the major unit (¥1300 -> amount 1300).
// Dividing by 100 everywhere silently breaks those currencies, so all
// minor-unit -> display conversions should go through here.
//
// Source: https://docs.stripe.com/currencies#zero-decimal
// NOTE: three-decimal currencies (KWD, BHD, OMR, etc.) are intentionally not
// handled here — we don't support any, and they need bespoke rounding rules.
export const STRIPE_ZERO_DECIMAL_CURRENCIES = new Set<string>([
  "BIF",
  "CLP",
  "DJF",
  "GNF",
  "JPY",
  "KMF",
  "KRW",
  "MGA",
  "PYG",
  "RWF",
  "UGX",
  "VND",
  "VUV",
  "XAF",
  "XOF",
  "XPF",
]);

/** Whether Stripe stores this currency's amounts already in the major unit. */
export function isStripeZeroDecimal(currency: string): boolean {
  return STRIPE_ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase());
}

/**
 * Convert a Stripe amount (smallest currency unit) to its display value in the
 * major unit. e.g. (899, "usd") -> 8.99 ; (1300, "jpy") -> 1300.
 */
export function stripeAmountToMajor(amount: number, currency: string): number {
  return isStripeZeroDecimal(currency) ? amount : amount / 100;
}

/**
 * Convert a major-unit amount to the Stripe smallest-unit amount, for creating
 * prices/coupons. e.g. (8.99, "usd") -> 899 ; (1300, "jpy") -> 1300.
 */
export function majorToStripeAmount(amount: number, currency: string): number {
  return isStripeZeroDecimal(currency) ? Math.round(amount) : Math.round(amount * 100);
}
